import { useState, useEffect, useRef, useCallback } from 'react'

interface SpeedtestResponse {
  timestamp: number
  test_size: number
  payload: string  // Base64 encoded
}

interface SpeedtestMetrics {
  latency: number  // ms
  throughput: number  // Mbps
  status: 'slow' | 'medium' | 'fast'
}

const SPEEDTEST_INTERVAL = 60000 // 60 secunde
const TEST_PAYLOAD_SIZE = 100000 // 100KB

// Threshold-uri optimizate pentru scroll DICOM
const LATENCY_THRESHOLDS = {
  fast: 50,   // < 50ms = Rapidă
  medium: 150 // 50-150ms = Medie, > 150ms = Slab
}

const THROUGHPUT_THRESHOLDS = {
  fast: 10,  // > 10 Mbps = Rapidă
  medium: 3  // 3-10 Mbps = Medie, < 3 Mbps = Slab
}

function calculateStatus(latency: number, throughput: number): 'slow' | 'medium' | 'fast' {
  const latencyStatus = latency > LATENCY_THRESHOLDS.medium ? 'slow' : latency > LATENCY_THRESHOLDS.fast ? 'medium' : 'fast'
  const throughputStatus = throughput < THROUGHPUT_THRESHOLDS.medium ? 'slow' : throughput < THROUGHPUT_THRESHOLDS.fast ? 'medium' : 'fast'
  
  // Worst case approach - luăm statusul cel mai rău
  if (latencyStatus === 'slow' || throughputStatus === 'slow') return 'slow'
  if (latencyStatus === 'medium' || throughputStatus === 'medium') return 'medium'
  return 'fast'
}

export const useSpeedtest = () => {
  const [metrics, setMetrics] = useState<SpeedtestMetrics | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<number | null>(null)

  const runSpeedtest = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const baseUrl = `${window.location.protocol}//${window.location.host}`
      const clientStartTime = performance.now()

      // Request pentru speedtest
      const response = await fetch(
        `${baseUrl}/api/v1/public/speedtest?size=${TEST_PAYLOAD_SIZE}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const clientEndTime = performance.now()
      
      // Calculăm timpul total de request (include download-ul payload-ului)
      const totalTime = clientEndTime - clientStartTime // milliseconds
      
      const data: SpeedtestResponse = await response.json()

      // Calcul latency (RTT aproximativ)
      // Folosim timpul total de request ca aproximare pentru RTT
      const latency = totalTime

      // Calcul throughput
      // Payload size este în bytes (din response)
      const payloadBytes = data.test_size
      
      // Throughput în Mbps (megabits per second)
      // Folosim timpul total de request ca proxy pentru download time
      // Convertim bytes la bits (*8) și apoi la Mbps (/1000000)
      const downloadTimeSeconds = totalTime / 1000 // convert to seconds
      const throughput = downloadTimeSeconds > 0 
        ? (payloadBytes * 8) / (downloadTimeSeconds * 1000000) 
        : 0

      const status = calculateStatus(latency, throughput)

      setMetrics({
        latency: Math.round(latency),
        throughput: Math.round(throughput * 10) / 10, // Round la 1 decimal
        status
      })
    } catch (err) {
      console.warn('Speedtest failed:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
      // Nu resetăm metrics în caz de eroare - păstrăm ultimele valori
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Rulează imediat la mount
    runSpeedtest()

    // Apoi rulează periodic
    intervalRef.current = setInterval(() => {
      runSpeedtest()
    }, SPEEDTEST_INTERVAL)

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [runSpeedtest])

  return { metrics, loading, error, runSpeedtest }
}

